import { groundHeightAt, type ArenaDefinition } from "./arena.ts";
import type { AttackDef, Facing, Placement, ProjectileSpec } from "./combat.ts";
import { TICK_RATE } from "./constants.ts";
import { Movement as M } from "./movement.ts";

/** Height of a shot above the shooter's feet: chest level. */
export const PROJECTILE_HEIGHT = M.height * 0.6;

/**
 * A projectile in flight (server only: the clients draw the replicated
 * `ProjectileState`). Straight, no gravity: easy to read and to dodge
 * (docs/DECISIONS.md, D24). Horizontal, unless an aim-assisted shot locked
 * onto an enemy above or below.
 */
export interface ProjectileSim {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  radius: number;
  ticksLeft: number;
  /** A shot that crawls on the floor (`ProjectileSpec.ground`): its height above it; null for one that flies. */
  readonly crawl: number | null;
  /** The move that launched it: knockback, hitstun, meter. */
  readonly def: AttackDef;
}

/** The highest step a crawling shot climbs; a taller block is a wall. */
export const CRAWL_STEP = 0.6;

/** How far above or below the shooter's feet an aim-assisted shot still looks for a target. */
const AIM_RISE = 1.5;
/** A shot from a jump reaches whoever stands below. */
const AIM_DROP = 4;

/**
 * The enemy an aim-assisted shot locks onto (`ProjectileSpec.aimAssistDeg`):
 * within its reach, inside the cone around the facing, not too far above or
 * below. The smallest angle wins, the nearer breaking ties; null when nobody
 * is in the cone and the shot flies straight ahead.
 */
export function aimTarget<T extends Placement>(shooter: Facing, spec: ProjectileSpec, enemies: readonly T[]): T | null {
  const cone = spec.aimAssistDeg ?? 0;
  if (cone <= 0) return null;
  const reach = (spec.speed * spec.lifetime) / TICK_RATE + M.radius;
  const widest = (cone * Math.PI) / 180;
  let best: T | null = null;
  let bestScore = Infinity;
  for (const enemy of enemies) {
    const dx = enemy.x - shooter.x;
    const dz = enemy.z - shooter.z;
    const dy = enemy.y - shooter.y;
    const distance = Math.hypot(dx, dz);
    if (distance < 1e-3 || distance > reach || dy > AIM_RISE || dy < -AIM_DROP) continue;
    const angle = Math.acos(Math.max(-1, Math.min(1, (dx * shooter.fx + dz * shooter.fz) / distance)));
    if (angle > widest) continue;
    const score = angle / widest + (0.25 * distance) / reach;
    if (score < bestScore) {
      bestScore = score;
      best = enemy;
    }
  }
  return best;
}

/**
 * Spawns the shot of a ranged move just in front of the shooter, along its
 * facing; towards `aim` (an enemy's feet) when an aim-assisted shot locked on,
 * flying at the height it would have at the enemy's level.
 */
export function launchProjectile(shooter: Facing, def: AttackDef, aim?: Placement | null): ProjectileSim {
  const spec = def.projectile;
  if (!spec) throw new Error(`${def.name} non è una mossa a distanza`);
  const offset = M.radius + spec.radius;
  const height = spec.height ?? PROJECTILE_HEIGHT;
  let fx = shooter.fx;
  let fz = shooter.fz;
  let dy = 0;
  if (aim) {
    const dx = aim.x - shooter.x;
    const dz = aim.z - shooter.z;
    const distance = Math.hypot(dx, dz);
    if (distance > 1e-3) {
      fx = dx / distance;
      fz = dz / distance;
      dy = (aim.y - shooter.y) / Math.max(distance - offset, 0.5);
    }
  }
  // The speed is along the flight: a shot towards someone below flies down at the same pace.
  const scale = spec.speed / Math.hypot(1, dy);
  return {
    x: shooter.x + fx * offset,
    y: shooter.y + height,
    z: shooter.z + fz * offset,
    vx: fx * scale,
    vy: dy * scale,
    vz: fz * scale,
    radius: spec.radius,
    ticksLeft: spec.lifetime,
    crawl: spec.ground ? height : null,
    def,
  };
}

/** Moves a projectile by one step; false once it expired or struck a solid. */
export function stepProjectile(p: ProjectileSim, arena: ArenaDefinition, dt: number): boolean {
  if (p.ticksLeft <= 0) return false;
  p.ticksLeft -= 1;
  // Sub-steps, so a fast shot cannot skip through a thin pillar or a fighter.
  const dx = p.vx * dt;
  const dy = p.vy * dt;
  const dz = p.vz * dt;
  const steps = Math.max(1, Math.ceil(Math.sqrt(dx * dx + dy * dy + dz * dz) / p.radius));
  for (let i = 0; i < steps; i++) {
    p.x += dx / steps;
    p.y += dy / steps;
    p.z += dz / steps;
    if (p.crawl !== null) {
      // Up a low step, down a slope or a ledge; off the edge it falls into the void.
      const floor = groundHeightAt(arena, p.x, p.z, p.y - p.crawl + CRAWL_STEP);
      if (floor === null) return false;
      p.y = floor + p.crawl;
    }
    if (insideSolid(p, arena)) return false;
  }
  return true;
}

/** Ball against the fighter's cylinder. */
export function projectileHits(p: Pick<ProjectileSim, "x" | "y" | "z" | "radius">, target: Placement): boolean {
  if (p.y < target.y - p.radius || p.y > target.y + M.height + p.radius) return false;
  const dx = target.x - p.x;
  const dz = target.z - p.z;
  const reach = M.radius + p.radius;
  return dx * dx + dz * dz <= reach * reach;
}

/**
 * The previous step's path, `(fromX, fromY, fromZ)` → current position, against
 * a fighter: catches targets the shot passed through within one step.
 */
export function projectileSweepHits(p: ProjectileSim, fromX: number, fromZ: number, target: Placement, fromY = p.y - p.vy / TICK_RATE): boolean {
  const steps = Math.max(1, Math.ceil(Math.hypot(p.x - fromX, p.y - fromY, p.z - fromZ) / p.radius));
  for (let i = 1; i <= steps; i++) {
    const t = i / steps;
    const probe = { x: fromX + (p.x - fromX) * t, y: fromY + (p.y - fromY) * t, z: fromZ + (p.z - fromZ) * t, radius: p.radius };
    if (projectileHits(probe, target)) return true;
  }
  return false;
}

function insideSolid(p: ProjectileSim, arena: ArenaDefinition): boolean {
  // A crawling shot climbs a low block once it is over it, instead of striking its side.
  const climb = p.crawl === null ? -Infinity : p.y - p.crawl + CRAWL_STEP;
  for (const box of arena.solids) {
    if (p.y < box.min.y || p.y > box.max.y || box.max.y <= climb) continue;
    const cx = Math.max(box.min.x, Math.min(p.x, box.max.x));
    const cz = Math.max(box.min.z, Math.min(p.z, box.max.z));
    const dx = p.x - cx;
    const dz = p.z - cz;
    if (dx * dx + dz * dz < p.radius * p.radius * 0.25) return true;
  }
  return false;
}
